import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, clearSession, hasStoredSession, setSession, setUnauthenticatedHandler } from './api';

export type StaffRole = 'TECH_ADMIN' | 'SEC_ANALYST' | 'HR_REVIEWER' | 'AUDITOR';

export interface StaffSummary {
  id: string;
  email: string;
  fullName: string;
  department: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDED';
  roles: StaffRole[];
  mustChangePassword: boolean;
  lastLoginAt: string | null;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  mustChangePassword: boolean;
  user: StaffSummary;
}

interface SessionValue {
  user: StaffSummary | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  can: (...roles: StaffRole[]) => boolean;
  refreshUser: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      clearSession();
      setUser(null);
    });
  }, []);

  // A page reload keeps the refresh token, so the session is restored rather than forcing a
  // sign-in on every navigation.
  useEffect(() => {
    if (!hasStoredSession()) {
      setLoading(false);
      return;
    }
    api
      .get<StaffSummary>('/api/v1/auth/me')
      .then(setUser)
      .catch(() => clearSession())
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.post<TokenResponse>('/api/v1/auth/login', { email, password });
    setSession(response);
    setUser(response.user);
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await api.get<StaffSummary>('/api/v1/auth/me'));
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      can: (...roles: StaffRole[]) => Boolean(user && roles.some((role) => user.roles.includes(role))),
      refreshUser,
    }),
    [user, loading, signIn, signOut, refreshUser],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}
